import React, { useCallback, useMemo } from 'react';
import { Globe2, Stethoscope, Heart, Shield, Scale, BookOpen, Clock } from 'lucide-react';
import { useDiagramStore } from '../../../stores/diagramStore';
import { usePerspectiveStore } from '../../../stores/perspectiveStore';
import { FloatingLegendCard } from '../../../components/legend/FloatingLegendCard';
import { useTranslation } from '@/hooks/useTranslation';
import { buildGenogramViewpointLegendData, normalizeTag } from '../utils/genogramViewpointLegend';

const humanize = (value: string): string =>
  value
    .split('_')
    .map((part) => (part.length ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''))
    .join(' ');

export const GenogramHealthFocusLegend: React.FC = () => {
  const { t } = useTranslation('genogram');
  const nodes = useDiagramStore((s) => s.nodes);
  const edges = useDiagramStore((s) => s.edges);
  const perspective = usePerspectiveStore((s) => s.perspective);
  const focusCondition = usePerspectiveStore((s) => s.healthFocusCondition);
  const focusIndexPersonId = usePerspectiveStore((s) => s.healthFocusIndexPersonId);
  const healthPalette = usePerspectiveStore((s) => s.healthPalette);
  const culturePalette = usePerspectiveStore((s) => s.culturePalette);

  const healthTagLabel = useCallback(
    (tag: string) => {
      const key = normalizeTag(tag);
      return t(`settings.healthTagLabels.${key}` as any, {
        defaultValue: humanize(key),
      });
    },
    [t],
  );

  const cultureTagLabel = useCallback(
    (tag: string) => {
      const key = normalizeTag(tag);
      return t(`settings.cultureTagLabels.${key}` as any, {
        defaultValue: humanize(key),
      });
    },
    [t],
  );

  const legendData = useMemo(
    () =>
      buildGenogramViewpointLegendData({
        nodes,
        edges,
        culturePalette,
        healthPalette,
        healthFocusCondition: focusCondition,
        healthFocusIndexPersonId: focusIndexPersonId,
        healthTagLabel,
        cultureTagLabel,
      }),
    [nodes, edges, culturePalette, healthPalette, focusCondition, focusIndexPersonId, healthTagLabel, cultureTagLabel],
  );
  const stats = perspective === 'health' ? legendData.healthFocusStats : null;
  const culturalStats = perspective === 'cultural' ? legendData.culturalItems : null;
  const hasHealthLegend = perspective === 'health';

  const isNewViewpoint = ['relationship_dynamics', 'risk_services', 'legal_estate', 'research_quality', 'timeline_events'].includes(perspective);
  if (!hasHealthLegend && !culturalStats && !isNewViewpoint) return null;

  if (perspective === 'relationship_dynamics') {
    const legendItemKeys = [
      'fields.therapy.emotionalBonds.label',
      'fields.therapy.attachmentStyle.label',
      'fields.therapy.communicationStyle.label',
    ] as const;
    return (
      <FloatingLegendCard
        testId="tid.genogram.relationship_dynamics.legend"
        toggleTestId="tid.genogram.relationship_dynamics.legend.toggle"
        className="bottom-20 right-4"
        title={t('settings.perspectives.relationship_dynamics')}
        icon={<Heart className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />}
      >
        {legendItemKeys.map((key) => (
          <div key={key} className="text-xs text-gray-700 dark:text-gray-200" dir="auto">
            {t(key)}
          </div>
        ))}
      </FloatingLegendCard>
    );
  }

  if (perspective === 'risk_services') {
    const riskLegendLines = [
      t('fields.social.housingStatus.label'),
      t('fields.medical.substanceUse.label'),
      t('viewpointLegend.riskServices.childrenInCare', { defaultValue: 'Children in Care' }),
    ] as const;
    return (
      <FloatingLegendCard
        testId="tid.genogram.risk_services.legend"
        toggleTestId="tid.genogram.risk_services.legend.toggle"
        className="bottom-20 right-4"
        title={t('settings.perspectives.risk_services')}
        icon={<Shield className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />}
      >
        {riskLegendLines.map((line, i) => (
          <div key={i} className="text-xs text-gray-700 dark:text-gray-200" dir="auto">
            {line}
          </div>
        ))}
      </FloatingLegendCard>
    );
  }

  if (perspective === 'legal_estate') {
    const legalLegendLines = [
      t('viewpointLegend.legalEstate.wills', { defaultValue: 'Wills' }),
      t('viewpointLegend.legalEstate.guardianships', { defaultValue: 'Guardianships' }),
      t('fields.legal.powerOfAttorney.label'),
    ];
    return (
      <FloatingLegendCard
        testId="tid.genogram.legal_estate.legend"
        toggleTestId="tid.genogram.legal_estate.legend.toggle"
        className="bottom-20 right-4"
        title={t('settings.perspectives.legal_estate')}
        icon={<Scale className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />}
      >
        {legalLegendLines.map((line, i) => (
          <div key={i} className="text-xs text-gray-700 dark:text-gray-200" dir="auto">
            {line}
          </div>
        ))}
      </FloatingLegendCard>
    );
  }

  if (perspective === 'research_quality') {
    const researchLegendLines = [
      t('viewpointLegend.researchQuality.completeness', { defaultValue: 'Completeness' }),
      t('fields.research.consentStatus.label'),
      t('viewpointLegend.researchQuality.dataSource', { defaultValue: 'Data Source' }),
    ];
    return (
      <FloatingLegendCard
        testId="tid.genogram.research_quality.legend"
        toggleTestId="tid.genogram.research_quality.legend.toggle"
        className="bottom-20 right-4"
        title={t('settings.perspectives.research_quality')}
        icon={<BookOpen className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />}
      >
        {researchLegendLines.map((line, i) => (
          <div key={i} className="text-xs text-gray-700 dark:text-gray-200" dir="auto">
            {line}
          </div>
        ))}
      </FloatingLegendCard>
    );
  }

  if (perspective === 'timeline_events') {
    const timelineLegendLines = [
      t('fields.birthYear.label'),
      t('fields.deathYear.label'),
      t('viewpointLegend.timelineEvents.lifeEvents', { defaultValue: 'Life Events' }),
    ];
    return (
      <FloatingLegendCard
        testId="tid.genogram.timeline_events.legend"
        toggleTestId="tid.genogram.timeline_events.legend.toggle"
        className="bottom-20 right-4"
        title={t('settings.perspectives.timeline_events')}
        icon={<Clock className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />}
      >
        {timelineLegendLines.map((line, i) => (
          <div key={i} className="text-xs text-gray-700 dark:text-gray-200" dir="auto">
            {line}
          </div>
        ))}
      </FloatingLegendCard>
    );
  }

  if (perspective === 'cultural') {
    return (
      <FloatingLegendCard
        testId="tid.genogram.cultural.legend"
        toggleTestId="tid.genogram.cultural.legend.toggle"
        className="bottom-20 right-4"
        title={t('culturalFocusLegend.title')}
        icon={<Globe2 className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />}
      >
        {(culturalStats ?? []).length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-gray-400">{t('culturalFocusLegend.noTags')}</div>
        ) : (
          (culturalStats ?? []).map((item) => (
            <div key={item.tag} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
              <span className="inline-flex h-3 w-3 rounded-sm" style={{ backgroundColor: item.color }} />
              <span className="flex-1">{cultureTagLabel(item.tag)}</span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400">{item.count}</span>
            </div>
          ))
        )}
      </FloatingLegendCard>
    );
  }

  if (perspective !== 'health') return null;
  const showFocusStats = Boolean(focusCondition && stats);
  const focusStats = showFocusStats ? stats : null;

  return (
    <FloatingLegendCard
      testId="tid.genogram.healthfocus.legend"
      toggleTestId="tid.genogram.healthfocus.legend.toggle"
      className="bottom-20 right-4"
      title={
        focusCondition
          ? t('healthFocusLegend.focusTitle', { condition: healthTagLabel(focusCondition) })
          : t('healthFocusLegend.title')
      }
      icon={<Stethoscope className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />}
    >
      {focusStats?.indexName ? (
        <div className="text-[11px] text-gray-500 dark:text-gray-400">
          {t('healthFocusLegend.indexPersonLabel')}{' '}
          <span className="font-medium text-gray-700 dark:text-gray-200">{focusStats.indexName}</span>
        </div>
      ) : null}
      {showFocusStats ? (
        <>
          <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
            <span
              className="inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px]"
              style={{ backgroundColor: focusStats?.color, borderColor: focusStats?.color, color: '#fff' }}
            >
              {focusStats?.icon}
            </span>
            <span className="flex-1">{t('healthFocusLegend.directMarkers')}</span>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">{focusStats?.direct ?? 0}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
            <span
              className="inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px]"
              style={{ borderColor: focusStats?.color, color: focusStats?.color, borderStyle: 'dashed' }}
            >
              {focusStats?.icon}
            </span>
            <span className="flex-1">{t('healthFocusLegend.inheritedRisk')}</span>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">{focusStats?.inheritedRisk ?? 0}</span>
          </div>
        </>
      ) : (
        <>
          {legendData.healthItems.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('healthFocusLegend.noTags')}</div>
          ) : (
            legendData.healthItems.map((item) => (
              <div key={item.tag} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
                <span
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px]"
                  style={{ backgroundColor: item.color, borderColor: item.color, color: '#fff' }}
                >
                  {item.icon || '•'}
                </span>
                <span className="flex-1">{healthTagLabel(item.tag)}</span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{item.count}</span>
              </div>
            ))
          )}
        </>
      )}
    </FloatingLegendCard>
  );
};
